import { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import PropTypes from 'prop-types'
import { Info, ArrowRight, Camera, ChevronDown, X } from 'lucide-react'
import { Autocomplete, TextField } from '@mui/material'

import { FormModal } from '@components/branded-modal'
import { AdasBox, AdasRadioGroup, AdasSearchMenu, AdasTypography } from '@components/wrapper-components'
import { useCenterStore } from '@caradasstore/CenterStore'
import { useUserStore } from '@caradasstore/UserStore'
import { useMessageStore } from '@caradasstore/MessageStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import {
  createSupportTicket,
  getReferenceNumberBySearch,
  getSupportTicketsConstants,
  getUserList,
  uploadSupportPhoto
} from '../../../api/api'
import { Logger } from '../../../logger'
import { GENERIC_MESSAGES, LABEL_DISPLAY, MAX_FILE_SIZE_MB } from '../../../constants'
import { RaisedTypesEnum, SupportRecordTypeEnums, SupportRequestEnums } from 'enums/enums'

const MAX_DESCRIPTION = 500
const ALLOWED_PHOTO_TYPES = ['image/jpeg', 'image/png', 'image/jpg']

// Common field styling so all step-2 inputs/selects/textareas line up consistently.
const FIELD_LABEL = 'mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-[#6B7280]'
const INPUT_CLS =
  'h-11 w-full rounded-lg border border-[#D1D5D8] bg-white px-3 text-[14px] text-[#0A1628] placeholder-[#9CA3AF] focus:border-[#1F6FEB] focus:outline-none disabled:bg-[#F8FAFC] disabled:opacity-70'
const SELECT_CLS =
  'h-11 w-full appearance-none rounded-lg border border-[#D1D5D8] bg-white pl-3 pr-9 text-[14px] text-[#0A1628] focus:border-[#1F6FEB] focus:outline-none disabled:bg-[#F8FAFC]'
const TEXTAREA_CLS =
  'w-full resize-y rounded-lg border border-[#D1D5D8] bg-white p-3 text-[14px] text-[#0A1628] placeholder-[#9CA3AF] focus:border-[#1F6FEB] focus:outline-none'
const HELPER_CLS = 'mt-1 text-[11px] text-[#6B7280]'

// Record # dropdown is populated from the location's QWI records and must show the
// most recent first (CAR-3686). Tolerate the various date field names the
// reference-number endpoint may return; records without a date sort last.
const RECORD_DATE_KEYS = ['created_at', 'createdAt', 'date_created', 'dateCreated', 'created_date', 'created']
const recordCreatedAt = (record) => {
  for (const key of RECORD_DATE_KEYS) {
    if (record?.[key]) {
      const t = new Date(record[key]).getTime()
      if (!Number.isNaN(t)) return t
    }
  }
  return 0
}
const sortRecordsByNewest = (records) => [...(records || [])].sort((a, b) => recordCreatedAt(b) - recordCreatedAt(a))

/** Multi-step Create Support Request modal.
 *  Step 1: pick support type + request type (+ a referenced record).
 *  Step 2 (matches the new design): description + photos + record preview +
 *    contact info, then submit via `createSupportTicket`.
 *  The previous diagnostic-tree intermediary was removed. */
export const CreateSupportRequestModal = ({ open, onClose, referenceNumber }) => {
  const history = useHistory()
  const ccId = useCenterStore((s) => s.ccId)
  const currentUser = useUserStore((s) => s.currentUser)
  const setLoading = useLoadingStore((s) => s.setLoading)
  const addMessage = useMessageStore((s) => s.addMessage)

  const [step, setStep] = useState(1)

  // Step 1 state — selection.
  const [supportType, setSupportType] = useState('')
  const [requestType, setRequestType] = useState('')
  const [selectedRecord, setSelectedRecord] = useState('')
  const [recordList, setRecordList] = useState([])
  const [isDisableNextButton, setIsDisableNextButton] = useState(true)
  const [supportRequestConstants, setSupportRequestConstants] = useState({})
  const [searchRecord, setSearchRecord] = useState('')
  const [recordInput, setRecordInput] = useState('')
  const [userOverride, setUserOverride] = useState(false)

  // Step 2 state — details + submit.
  const [problemArea, setProblemArea] = useState('')
  const [dtc, setDtc] = useState('')
  const [dtcDescription, setDtcDescription] = useState('')
  const [calibrationType, setCalibrationType] = useState('')
  const [device, setDevice] = useState('')
  const [browser, setBrowser] = useState('')
  const [problemDescription, setProblemDescription] = useState('')
  const [photo, setPhoto] = useState(null) // primary support photo (File)
  const [userList, setUserList] = useState([])
  const [contactUser, setContactUser] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  // Reset on each (re-)open.
  useEffect(() => {
    if (!open) return
    setStep(1)
    setSelectedRecord('')
    setRecordList([])
    setUserOverride(false)
    setProblemArea('')
    setDtc('')
    setDtcDescription('')
    setCalibrationType('')
    setDevice('')
    setBrowser('')
    setProblemDescription('')
    setPhoto(null)
    if (referenceNumber) {
      setSupportType(SupportRequestEnums.CALIBRATION_DIAGNOSTIC)
      setRequestType(SupportRecordTypeEnums.QUOTE_WORKORDER_INVOICE)
      setRecordInput(referenceNumber)
      setSearchRecord(referenceNumber)
      setIsDisableNextButton(false)
    } else {
      setSupportType('')
      setRequestType('')
      setRecordInput('')
      setSearchRecord('')
      setIsDisableNextButton(true)
    }
  }, [open, referenceNumber])

  // Auto-select first record when seeded with a referenceNumber.
  useEffect(() => {
    if (!open || !referenceNumber || userOverride) return
    if (recordList.length > 0) {
      setSelectedRecord(recordList[0])
      setIsDisableNextButton(false)
    }
  }, [open, referenceNumber, recordList, userOverride])

  // When we're on step 2 with a selectedRecord that's missing vehicle fields
  // (the BE's reference-number list can return slim items), refetch and pick
  // the matching record so the preview can render year/make/model/vin/image.
  // Mirrors the same fetch the old CreateSupportRequestPage used.
  useEffect(() => {
    if (step !== 2 || !ccId || !selectedRecord?.reference_number) return undefined
    const v = selectedRecord
    const hasVehicleData = v.image_url || v.year || v.make || v.model || v.vin || v.vehicle
    if (hasVehicleData) return undefined
    const fetch = async () => {
      try {
        const res = await getReferenceNumberBySearch({ ccId, search: selectedRecord.reference_number })
        const match = (res?.data || []).find((it) => it.reference_number === selectedRecord.reference_number)
        if (match) setSelectedRecord(match)
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'CreateSupportRequestModal', method: 'refetchVehicle' } })
      }
    }
    fetch()
    return undefined
  }, [step, ccId, selectedRecord])

  // Constants for the type/request-type lists.
  useEffect(() => {
    if (!open) return undefined
    const fetch = async () => {
      try {
        const res = await getSupportTicketsConstants()
        setSupportRequestConstants(res?.data || {})
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'CreateSupportRequestModal', method: 'getSupportTicketsConstants' } })
      }
    }
    fetch()
    return undefined
  }, [open])

  // User list for the contact picker (admins filtered out for non-admins).
  useEffect(() => {
    if (!open || !ccId) return undefined
    const fetch = async () => {
      try {
        const res = await getUserList({ ccid: ccId })
        if (res?.status === 200) setUserList(res.data || [])
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'CreateSupportRequestModal', method: 'getUserList' } })
      }
    }
    fetch()
    return undefined
  }, [open, ccId])

  // Default the contact to the current user.
  useEffect(() => {
    if (open && currentUser && !contactUser) setContactUser(currentUser)
  }, [open, currentUser, contactUser])

  // Reference-number list. Populate the Record # dropdown from the location's QWI
  // records so it's filled without requiring a search (an empty search returns the
  // full list); typing still narrows the results server-side. Always sorted
  // newest → oldest in the FE so the most recent records are at the top (CAR-3686).
  useEffect(() => {
    if (!ccId || requestType !== SupportRecordTypeEnums.QUOTE_WORKORDER_INVOICE) return undefined
    const fetch = async () => {
      try {
        const res = await getReferenceNumberBySearch({ ccId, search: searchRecord })
        setRecordList(sortRecordsByNewest(res?.data || []))
      } catch (error) {
        Logger.error({ message: error, payload: { file: 'CreateSupportRequestModal', method: 'getReferenceNumberBySearch' } })
      }
    }
    fetch()
    return undefined
  }, [ccId, searchRecord, requestType])

  useEffect(() => {
    const t = setTimeout(() => setSearchRecord(recordInput), 300)
    return () => clearTimeout(t)
  }, [recordInput])

  const handleSupportType = useCallback((e) => {
    const next = e.target.value
    setIsDisableNextButton(true)
    setSelectedRecord('')
    setRecordList([])
    setSupportType(next)
    if (next === SupportRequestEnums.CALIBRATION_DIAGNOSTIC) {
      setRequestType(SupportRecordTypeEnums.QUOTE_WORKORDER_INVOICE)
    } else {
      setRequestType('')
    }
  }, [])

  const onRequestTypeChange = (value) => {
    setIsDisableNextButton(value === SupportRequestEnums.QUOTE_WORKORDER_INVOICE)
    setRequestType(value)
    setSelectedRecord('')
  }

  const handleTicketNumberInputChange = useCallback((e, value) => {
    // Clearing the selection resets the input, which refetches the full location
    // list; keep the current list in place meanwhile so the dropdown stays filled.
    setRecordInput(value?.reference_number)
    setSelectedRecord(value)
    setIsDisableNextButton(!value)
    setUserOverride(true)
  }, [])

  const handleBlur = useCallback(() => {
    // Reset the typed text when nothing was picked; keep the list populated (the
    // reset drives a refetch of the location's full QWI list) so reopening the
    // dropdown still shows the records without requiring a search.
    if (!selectedRecord) setRecordInput('')
  }, [selectedRecord])

  const searchRecordInputHandler = useCallback((e) => {
    setRecordInput(e.target.value)
    setUserOverride(true)
  }, [])

  // A record is only required when the chosen request type points to a Quote/WO/Invoice.
  // Car ADAS+ IT requests (and any future request type that doesn't reference a record)
  // can proceed without one.
  const needsRecord = requestType === SupportRecordTypeEnums.QUOTE_WORKORDER_INVOICE

  const handleNext = () => {
    if (needsRecord && !selectedRecord) return
    setStep(2)
  }
  const handleGoBack = () => setStep(1)

  const handlePhotoPick = (setter) => (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
      addMessage({ text: 'Please upload a JPG or PNG image.' })
      return
    }
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      addMessage({ text: `File size exceeds the ${MAX_FILE_SIZE_MB}MB limit.` })
      return
    }
    setter(file)
  }

  const handleSubmit = useCallback(async () => {
    if (submitting) return
    setSubmitting(true)
    setLoading(true)
    let reqBody = {}
    try {
      reqBody = {
        type: supportType,
        calibration_center_id: ccId,
        problem_description: problemDescription,
        contact_user_id: contactUser?.id
      }
      // Record-linked fields only when the request actually references a Quote/WO/Invoice.
      if (selectedRecord) {
        reqBody.reference_number = selectedRecord.reference_number
        reqBody.qwi_id = selectedRecord.qwi_id
        reqBody.raised_stage = RaisedTypesEnum[selectedRecord.table_name]
      }
      if (supportType === SupportRequestEnums.CALIBRATION_DIAGNOSTIC) {
        reqBody.record_type = requestType
        reqBody.problem_area = problemArea
        reqBody.dtc = dtc
        reqBody.dtc_description = dtcDescription
        reqBody.calibration_type = calibrationType
      } else if (supportType === SupportRequestEnums.CAR_ADASPLUS_TECHNOLOGY) {
        reqBody.it_request_type = requestType
        // device/browser must be valid slugs from `supportDevices`/`supportBrowsers`.
        // Empty string is acceptable to the backend (`.isEmpty()` evaluates true).
        reqBody.device = device || ''
        reqBody.browser = browser || ''
      }
      const res = await createSupportTicket(reqBody)
      if (res?.status === 200) {
        const ticketId = res.data?.supportTicket?.id
        const photos = [photo].filter(Boolean)
        await Promise.all(
          photos.map((file, idx) => {
            return uploadSupportPhoto({ file, attachmentNo: idx, ticketId }).catch((err) => {
              Logger.error({ message: err, payload: { file: 'CreateSupportRequestModal', method: 'uploadSupportPhoto' } })
            })
          })
        )
        addMessage({ text: 'Support request submitted.' })
        onClose?.()
        history.push('/support')
      }
    } catch (error) {
      addMessage({ text: 'Something went wrong! Please try again.' })
      Logger.error({ message: error, payload: { file: 'CreateSupportRequestModal', method: 'handleSubmit', reqBody } })
    } finally {
      setSubmitting(false)
      setLoading(false)
    }
  }, [
    submitting,
    setLoading,
    supportType,
    requestType,
    ccId,
    problemDescription,
    selectedRecord,
    contactUser,
    photo,
    problemArea,
    dtc,
    dtcDescription,
    calibrationType,
    device,
    browser,
    addMessage,
    history,
    onClose
  ])

  // ─── footers per step ──────────────────────────────────────────────────────
  const step1Footer = (
    <div className='flex items-center justify-end gap-3'>
      <button
        type='button'
        onClick={onClose}
        className='inline-flex items-center rounded-lg px-4 py-2 text-[13px] font-bold uppercase tracking-wide text-[#1F6FEB] hover:bg-[#F8FAFC]'
      >
        Cancel
      </button>
      <button
        type='button'
        disabled={isDisableNextButton}
        onClick={handleNext}
        className='inline-flex items-center rounded-lg bg-sidebar px-5 py-2.5 text-[13px] font-bold uppercase tracking-wide text-white transition-colors hover:bg-sidebar/90 disabled:cursor-not-allowed disabled:opacity-50'
      >
        Next
      </button>
    </div>
  )

  const step2Footer = (
    <div className='flex items-center justify-end gap-3'>
      <button
        type='button'
        onClick={onClose}
        className='inline-flex items-center rounded-lg px-4 py-2 text-[13px] font-bold uppercase tracking-wide text-[#1F6FEB] hover:bg-[#F8FAFC]'
      >
        Cancel
      </button>
      <button
        type='button'
        onClick={handleGoBack}
        className='inline-flex items-center rounded-lg border border-[#1F6FEB] bg-white px-5 py-2.5 text-[13px] font-bold uppercase tracking-wide text-[#1F6FEB] hover:bg-[#F8FAFC]'
      >
        Go Back
      </button>
      <button
        type='button'
        disabled={
          submitting ||
          !problemDescription.trim() ||
          !contactUser?.id ||
          (needsRecord && !selectedRecord) ||
          (supportType === SupportRequestEnums.CALIBRATION_DIAGNOSTIC && (!problemArea || !calibrationType))
        }
        onClick={handleSubmit}
        className='inline-flex items-center rounded-lg bg-sidebar px-5 py-2.5 text-[13px] font-bold uppercase tracking-wide text-white transition-colors hover:bg-sidebar/90 disabled:cursor-not-allowed disabled:opacity-50'
      >
        Submit Request
      </button>
    </div>
  )

  return (
    <FormModal
      open={open}
      onClose={onClose}
      title={step === 1 ? 'Create Support Request' : 'Submit Support Request'}
      eyebrow={step === 2 ? `Step ${step} / 2` : undefined}
      footer={step === 1 ? step1Footer : step2Footer}
      width='max-w-[760px]'
    >
      {step === 1 && (
        <>
          {/* Yellow info banner */}
          <div className='mb-5 rounded-lg bg-[#FEF7CD] p-3'>
            <div className='flex items-start gap-2'>
              <Info className='mt-0.5 h-4 w-4 flex-none text-[#B45309]' />
              <div className='min-w-0 flex-1 text-[13px] text-[#1A1B1F]'>
                {GENERIC_MESSAGES.SUPPORT_REQUESTING_ASSITANCE}
                <button
                  type='button'
                  onClick={() => {
                    onClose?.()
                    history.push('/help')
                  }}
                  className='ml-2 inline-flex items-center gap-1 text-[12px] font-bold uppercase tracking-wide text-[#1F6FEB] hover:underline'
                >
                  {LABEL_DISPLAY.HELP_SECTION_LABEL}
                  <ArrowRight className='h-3 w-3' />
                </button>
              </div>
            </div>
          </div>

          <div className='mb-4'>
            <div className='mb-2 text-[11px] font-bold uppercase tracking-wide text-[#6B7280]'>{LABEL_DISPLAY.SUPPORT_TYPE_LABEL}</div>
            {supportRequestConstants?.supportTypes && (
              <AdasRadioGroup
                options={(supportRequestConstants.supportTypes || []).map((item) => ({ value: item.slug, label: item.type }))}
                row
                name='supportType'
                value={supportType}
                onChange={handleSupportType}
              />
            )}
          </div>

          {supportType && (
            <div className='mb-4'>
              <div className='mb-2 text-[11px] font-bold uppercase tracking-wide text-[#6B7280]'>{LABEL_DISPLAY.REQUEST_TYPE_LABEL}</div>
              {supportType === SupportRequestEnums.CALIBRATION_DIAGNOSTIC && (
                <AdasRadioGroup
                  options={(supportRequestConstants?.supportRecordTypes || [])
                    .filter((item) => item.slug !== SupportRecordTypeEnums.INFORMATION_REQUEST)
                    .map((item) => ({ value: item.slug, label: item.recordType }))}
                  row
                  name='requestType'
                  value={requestType}
                  onChange={(e) => onRequestTypeChange(e.target.value)}
                />
              )}
              {supportType === SupportRequestEnums.CAR_ADASPLUS_TECHNOLOGY && (
                <AdasSearchMenu
                  fullWidth
                  id='it-RequestType'
                  select
                  label='Type'
                  className='mt-2 w-full'
                  variant='outlined'
                  onChange={(e) => onRequestTypeChange(e.target.value)}
                  name='requestType'
                  value={requestType}
                  options={(supportRequestConstants?.supportItRequestTypes || []).map((item) => ({
                    value: item.slug,
                    name: item.itRequestType
                  }))}
                />
              )}
            </div>
          )}

          {requestType === SupportRequestEnums.QUOTE_WORKORDER_INVOICE && (
            <div className='mb-4'>
              <Autocomplete
                id='record-search'
                options={recordList}
                getOptionLabel={(option) => option?.reference_number || ''}
                value={selectedRecord || null}
                isOptionEqualToValue={(option, value) => option?.reference_number === value?.reference_number}
                onBlur={handleBlur}
                onInput={searchRecordInputHandler}
                onChange={handleTicketNumberInputChange}
                renderInput={(params) => <TextField {...params} label='Record #' variant='outlined' />}
              />
            </div>
          )}

          {/* Vehicle preview after selection — matches the original SupportRequestPage. */}
          {selectedRecord &&
            (() => {
              const v = selectedRecord.vehicle || selectedRecord
              const vehicleLine = [v?.year, v?.make, v?.model].filter(Boolean).join(' ')
              const imageUrl = v?.image_url || v?.imageUrl
              return (
                <div className='mt-4 grid grid-cols-1 items-center gap-4 rounded-lg border border-[#EBEDF0] bg-[#F8FAFC] p-4 sm:grid-cols-2'>
                  <div>
                    <div className='text-[13px] font-bold uppercase tracking-wide text-[#1F6FEB]'>
                      RECORD #{selectedRecord.reference_number}
                    </div>
                    {vehicleLine && <div className='mt-2 text-[15px] font-bold text-[#0A1628]'>{vehicleLine}</div>}
                    {v?.vin && <div className='mt-1 font-mono text-[12px] text-[#6B7280]'>VIN: {v.vin}</div>}
                  </div>
                  {imageUrl && (
                    <div className='flex items-center justify-center sm:justify-end'>
                      <img
                        src={imageUrl}
                        alt={vehicleLine || 'Vehicle'}
                        className='h-auto max-h-[140px] w-auto max-w-[260px] object-contain'
                      />
                    </div>
                  )}
                </div>
              )
            })()}
        </>
      )}

      {step === 2 && (
        <>
          {/* DETAILS — Cal-Diagnostic specific */}
          {supportType === SupportRequestEnums.CALIBRATION_DIAGNOSTIC && (
            <>
              <div className='mb-3 text-[11px] font-bold uppercase tracking-wide text-[#6B7280]'>Details</div>
              <div className='mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <div>
                  <label htmlFor='problem-area' className={FIELD_LABEL}>
                    Problem Area <span className='text-[#DC2626]'>*</span>
                  </label>
                  <div className='relative'>
                    <select id='problem-area' value={problemArea} onChange={(e) => setProblemArea(e.target.value)} className={SELECT_CLS}>
                      <option value=''>Select…</option>
                      {(supportRequestConstants?.supportProblemAreas || []).map((item) => (
                        <option key={item.slug} value={item.slug}>
                          {item.problemArea}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]' />
                  </div>
                </div>
                <div>
                  <label htmlFor='dtc' className={FIELD_LABEL}>
                    Diagnostic Trouble Code <span className='text-[#DC2626]'>*</span>
                  </label>
                  <input
                    id='dtc'
                    type='text'
                    value={dtc}
                    onChange={(e) => setDtc(e.target.value.slice(0, 40))}
                    maxLength={40}
                    className={INPUT_CLS}
                  />
                  <div className={HELPER_CLS}>40 characters max, {40 - dtc.length} remaining</div>
                </div>
              </div>

              <div className='mb-4'>
                <label htmlFor='code-description' className={FIELD_LABEL}>
                  Code Description <span className='font-normal normal-case text-[#9CA3AF]'>(Optional)</span>
                </label>
                <textarea
                  id='code-description'
                  value={dtcDescription}
                  onChange={(e) => setDtcDescription(e.target.value.slice(0, 500))}
                  rows={3}
                  maxLength={500}
                  className={TEXTAREA_CLS}
                />
                <div className={HELPER_CLS}>500 characters max, {500 - dtcDescription.length} remaining</div>
              </div>

              <div className='mb-5'>
                <label htmlFor='calibration-type' className={FIELD_LABEL}>
                  Calibration Type <span className='text-[#DC2626]'>*</span>
                </label>
                <div className='relative'>
                  <select
                    id='calibration-type'
                    value={calibrationType}
                    onChange={(e) => setCalibrationType(e.target.value)}
                    className={SELECT_CLS}
                  >
                    <option value=''>Select…</option>
                    {(supportRequestConstants?.calibrationCategoryTypes || []).map((item) => (
                      <option key={item.slug} value={item.slug}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]' />
                </div>
              </div>
            </>
          )}

          {/* Car ADAS+ IT specific — device + browser */}
          {supportType === SupportRequestEnums.CAR_ADASPLUS_TECHNOLOGY && (
            <>
              <div className='mb-3 text-[11px] font-bold uppercase tracking-wide text-[#6B7280]'>Details</div>
              <div className='mb-5 grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <div>
                  <label htmlFor='it-device' className={FIELD_LABEL}>
                    Device <span className='font-normal normal-case text-[#9CA3AF]'>(Optional)</span>
                  </label>
                  <div className='relative'>
                    <select id='it-device' value={device} onChange={(e) => setDevice(e.target.value)} className={SELECT_CLS}>
                      <option value=''>Select…</option>
                      {(supportRequestConstants?.supportDevices || []).map((item) => (
                        <option key={item.slug} value={item.slug}>
                          {item.device}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]' />
                  </div>
                </div>
                <div>
                  <label htmlFor='it-browser' className={FIELD_LABEL}>
                    Web Browser <span className='font-normal normal-case text-[#9CA3AF]'>(Optional)</span>
                  </label>
                  <div className='relative'>
                    <select id='it-browser' value={browser} onChange={(e) => setBrowser(e.target.value)} className={SELECT_CLS}>
                      <option value=''>Select…</option>
                      {(supportRequestConstants?.supportBrowsers || []).map((item) => (
                        <option key={item.slug} value={item.slug}>
                          {item.browser}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]' />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Problem Description */}
          <div className='mb-5'>
            <label htmlFor='support-description' className={FIELD_LABEL}>
              Problem Description <span className='text-[#DC2626]'>*</span>
            </label>
            <textarea
              id='support-description'
              value={problemDescription}
              onChange={(e) => setProblemDescription(e.target.value.slice(0, MAX_DESCRIPTION))}
              placeholder='Describe the issue...'
              rows={4}
              className={TEXTAREA_CLS}
            />
            <div className={HELPER_CLS}>
              {MAX_DESCRIPTION} characters max, {MAX_DESCRIPTION - problemDescription.length} remaining
            </div>
          </div>

          {/* Support photo */}
          <div className='mb-5'>
            <div className={FIELD_LABEL}>Support Photos</div>
            <PhotoSlot file={photo} onPick={handlePhotoPick(setPhoto)} onClear={() => setPhoto(null)} placeholder='Add Photo' />
          </div>

          {/* Record preview — text on left, vehicle image on right (no upload).
              Tolerate flat, nested `vehicle`, or camelCase response shapes. */}
          {selectedRecord &&
            (() => {
              const v = selectedRecord.vehicle || selectedRecord
              const year = v?.year
              const make = v?.make
              const model = v?.model
              const vin = v?.vin
              const imageUrl = v?.image_url || v?.imageUrl
              const vehicleLine = [year, make, model].filter(Boolean).join(' ')
              return (
                <div className='mb-5 grid grid-cols-1 items-center gap-4 sm:grid-cols-2'>
                  <div>
                    <div className='text-[13px] font-bold uppercase tracking-wide text-[#1F6FEB]'>
                      RECORD #{selectedRecord.reference_number}
                    </div>
                    {vehicleLine && <div className='mt-2 text-[15px] font-bold text-[#0A1628]'>{vehicleLine}</div>}
                    {vin && <div className='mt-1 font-mono text-[12px] text-[#6B7280]'>VIN: {vin}</div>}
                  </div>
                  {imageUrl && (
                    <div className='flex items-center justify-center sm:justify-end'>
                      <img
                        src={imageUrl}
                        alt={vehicleLine || 'Vehicle'}
                        className='h-auto max-h-[140px] w-auto max-w-[260px] object-contain'
                      />
                    </div>
                  )}
                </div>
              )
            })()}

          {/* Contact information */}
          <div className='mb-2'>
            <div className='mb-3 text-[11px] font-bold uppercase tracking-wide text-[#6B7280]'>Contact Information</div>
            <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
              <div>
                <label htmlFor='contact-name' className={FIELD_LABEL}>
                  Name
                </label>
                <div className='relative'>
                  <select
                    id='contact-name'
                    value={contactUser?.id || ''}
                    onChange={(e) => setContactUser(userList.find((u) => String(u.id) === e.target.value) || null)}
                    className={SELECT_CLS}
                  >
                    <option value=''>Select…</option>
                    {userList.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6B7280]' />
                </div>
              </div>
              <div className='hidden sm:block' />
              <div>
                <label htmlFor='contact-email' className={FIELD_LABEL}>
                  Email
                </label>
                <input id='contact-email' type='text' value={contactUser?.email || ''} disabled className={INPUT_CLS} />
              </div>
              <div>
                <label htmlFor='contact-phone' className={FIELD_LABEL}>
                  Phone
                </label>
                <input id='contact-phone' type='text' value={contactUser?.phone || ''} disabled className={INPUT_CLS} />
              </div>
            </div>
          </div>
        </>
      )}
    </FormModal>
  )
}

CreateSupportRequestModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  referenceNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

// ─── Local helper: a single photo-upload tile ───────────────────────────────
const PhotoSlot = ({ file, onPick, onClear, placeholder }) => {
  const previewUrl = file ? URL.createObjectURL(file) : null
  return (
    <label className='relative flex aspect-[16/5] min-h-[140px] w-full cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-[#D1D5D8] bg-[#F8FAFC] text-center transition-colors hover:bg-[#F1F4F8]'>
      {previewUrl ? (
        <>
          <AdasBox className='absolute inset-0 flex items-center justify-center overflow-hidden rounded-lg'>
            <img src={previewUrl} alt='Selected' className='max-h-full max-w-full object-contain' />
          </AdasBox>
          <button
            type='button'
            onClick={(e) => {
              e.preventDefault()
              onClear()
            }}
            className='absolute right-2 top-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-white shadow'
          >
            <X className='h-3.5 w-3.5 text-[#0A1628]' />
          </button>
        </>
      ) : (
        <>
          <Camera className='mb-1.5 h-5 w-5 text-[#1F6FEB]' />
          <AdasTypography className='text-[13px] font-bold uppercase tracking-wide text-[#1F6FEB]'>{placeholder}</AdasTypography>
        </>
      )}
      <input type='file' accept='image/jpeg,image/png,image/jpg' hidden onChange={onPick} />
    </label>
  )
}

PhotoSlot.propTypes = {
  file: PropTypes.object,
  onPick: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  placeholder: PropTypes.string
}
